import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useAppStore } from '@/stores/app';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/toast';
import {
  Globe,
  Users,
  UserCog,
  ListChecks,
  Swords,
  Sparkles,
  Activity,
  ShieldAlert,
  Target,
  Lock,
  Pencil,
  Save,
  X,
  Loader2,
} from 'lucide-react';
import type { AtlasTab } from '@starfox/shared-types';

interface AtlasAudience {
  primary?: string;
  secondary?: string[];
  ageRange?: [number, number] | null;
  gender?: 'all' | 'female' | 'male';
  regions?: string[];
  tags?: string[];
  locked?: boolean;
  lockedAt?: string | null;
}
interface AtlasOutlineNode {
  id: string;
  title: string;
  summary?: string;
  durationSec?: number;
}
interface AtlasCharacter {
  id: string;
  name: string;
  role?: string;
  archetype?: string;
  arc?: string;
  tags?: string[];
  desire?: string;
  fear?: string;
  flaw?: string;
  contradiction?: string;
  speaking_style?: string;
  subtext?: string;
  relations?: { target: string; kind: string; dynamic?: string; turning_point?: string }[];
}
interface AtlasNpc {
  id: string;
  name: string;
  function?: string;
}
interface AtlasConflictCell {
  a: string;
  b: string;
  intensity: number;
}
interface AtlasHook {
  sceneId: string;
  kind: string;
  density: number;
  episode?: number;
  desc?: string;
}
interface AtlasMoodPoint {
  t: number;
  arousal: number;
  valence: number;
  episode?: number;
  label?: string;
}
interface AtlasRiskItem {
  code: string;
  level: 'info' | 'warn' | 'block';
  message: string;
}
interface AtlasWorld {
  setting?: string;
  era?: string;
  region?: string;
  tone?: string;
  rules?: string[];
  themes?: string[];
}
interface StoryAtlasData {
  episodeId: string;
  versionVector?: Record<string, number>;
  audience?: AtlasAudience;
  world?: AtlasWorld;
  characters?: AtlasCharacter[];
  npc?: AtlasNpc[];
  outline?: AtlasOutlineNode[];
  conflict?: AtlasConflictCell[];
  hooks?: AtlasHook[];
  mood?: AtlasMoodPoint[];
  risk?: AtlasRiskItem[];
}

const tabs: { id: AtlasTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'audience', label: '人群', icon: Target },
  { id: 'world', label: '世界观', icon: Globe },
  { id: 'characters', label: '人物', icon: Users },
  { id: 'npc', label: 'NPC', icon: UserCog },
  { id: 'outline', label: '故事概览', icon: ListChecks },
  { id: 'conflict', label: '冲突矩阵', icon: Swords },
  { id: 'hooks', label: '爽点分析', icon: Sparkles },
  { id: 'mood', label: '情绪曲线', icon: Activity },
  { id: 'risk', label: '合规风险', icon: ShieldAlert },
];

interface Props {
  episodeId: string;
  status: 'draft' | 'initialized';
  onEditAudience: () => void;
}

export function StoryAtlasPanel({ episodeId, status, onEditAudience }: Props) {
  const { rightPanelTab, setAtlasTab } = useAppStore();
  const { data: atlas, isLoading } = useQuery<StoryAtlasData>({
    queryKey: ['atlas', episodeId],
    queryFn: () => api.get(`writer/atlas/${episodeId}`).json<StoryAtlasData>(),
    enabled: Boolean(episodeId),
    retry: false,
  });

  const vvSum = atlas?.versionVector
    ? Object.values(atlas.versionVector).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <aside className="hidden w-[380px] shrink-0 overflow-y-auto border-l border-outline-variant/20 bg-surface lg:block">
      <div className="sticky top-0 z-10 border-b border-outline-variant/20 bg-surface/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold tracking-tight">Story Atlas</h2>
          <span className="text-xs text-neutral-400">v_vector: {vvSum}</span>
        </div>
        <p className="mt-0.5 text-xs text-neutral-500">实时多维剧本视图 · 流式增量更新</p>
      </div>

      {isLoading ? (
        <div className="space-y-2 p-4">
          <div className="h-8 animate-pulse rounded bg-surface-variant" />
          <div className="h-24 animate-pulse rounded bg-surface-variant" />
          <div className="h-24 animate-pulse rounded bg-surface-variant" />
        </div>
      ) : (
        <Tabs value={rightPanelTab} onValueChange={(v) => setAtlasTab(v as AtlasTab)}>
          <div className="overflow-x-auto px-2 py-2">
            <TabsList className="w-max">
              {tabs.map((t) => (
                <TabsTrigger key={t.id} value={t.id} className="gap-1.5 text-xs">
                  <t.icon className="h-3.5 w-3.5" />
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="px-4 pb-6">
            <TabsContent value="audience">
              <AudienceTab audience={atlas?.audience} onEdit={onEditAudience} />
            </TabsContent>
            <TabsContent value="world">
              <DraftGate status={status}>
                <EditableTab
                  episodeId={episodeId}
                  tab="world"
                  value={atlas?.world ?? {}}
                  render={(v) => <WorldTab world={v as AtlasWorld} />}
                />
              </DraftGate>
            </TabsContent>
            <TabsContent value="characters">
              <DraftGate status={status}>
                <EditableTab
                  episodeId={episodeId}
                  tab="characters"
                  value={atlas?.characters ?? []}
                  render={(v) => <CharactersTab list={(v as AtlasCharacter[]) ?? []} />}
                />
              </DraftGate>
            </TabsContent>
            <TabsContent value="npc">
              <DraftGate status={status}>
                <EditableTab
                  episodeId={episodeId}
                  tab="npc"
                  value={atlas?.npc ?? []}
                  render={(v) => <NpcTab list={(v as AtlasNpc[]) ?? []} />}
                />
              </DraftGate>
            </TabsContent>
            <TabsContent value="outline">
              <DraftGate status={status}>
                <EditableTab
                  episodeId={episodeId}
                  tab="outline"
                  value={atlas?.outline ?? []}
                  render={(v) => <OutlineTab list={(v as AtlasOutlineNode[]) ?? []} />}
                />
              </DraftGate>
            </TabsContent>
            <TabsContent value="conflict">
              <DraftGate status={status}>
                <EditableTab
                  episodeId={episodeId}
                  tab="conflict"
                  value={atlas?.conflict ?? []}
                  render={(v) => <ConflictTab list={(v as AtlasConflictCell[]) ?? []} />}
                />
              </DraftGate>
            </TabsContent>
            <TabsContent value="hooks">
              <DraftGate status={status}>
                <EditableTab
                  episodeId={episodeId}
                  tab="hooks"
                  value={atlas?.hooks ?? []}
                  render={(v) => <HooksTab list={(v as AtlasHook[]) ?? []} />}
                />
              </DraftGate>
            </TabsContent>
            <TabsContent value="mood">
              <DraftGate status={status}>
                <EditableTab
                  episodeId={episodeId}
                  tab="mood"
                  value={atlas?.mood ?? []}
                  render={(v) => <MoodTab list={(v as AtlasMoodPoint[]) ?? []} />}
                />
              </DraftGate>
            </TabsContent>
            <TabsContent value="risk">
              <DraftGate status={status}>
                <EditableTab
                  episodeId={episodeId}
                  tab="risk"
                  value={atlas?.risk ?? []}
                  render={(v) => <RiskTab list={(v as AtlasRiskItem[]) ?? []} />}
                />
              </DraftGate>
            </TabsContent>
          </div>
        </Tabs>
      )}
    </aside>
  );
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1">
    <div className="text-xs uppercase tracking-wider text-outline">{label}</div>
    <div className="text-sm text-neutral-800">{children}</div>
  </div>
);

const Empty = ({ hint }: { hint: string }) => (
  <p className="rounded-lg border border-dashed border-outline-variant/40 px-3 py-4 text-center text-xs text-neutral-400">
    {hint}
  </p>
);

function DraftGate({
  status,
  children,
}: {
  status: 'draft' | 'initialized';
  children: React.ReactNode;
}) {
  if (status === 'initialized') return <>{children}</>;
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-outline-variant/40 bg-surface-variant/30 px-4 py-8 text-center">
      <Lock className="h-5 w-5 text-outline" />
      <p className="text-sm text-neutral-600">项目初始化成功后填充</p>
      <p className="text-xs text-neutral-400 leading-relaxed">
        立项 → 锁画像 → 项目初始化
      </p>
    </div>
  );
}

const TAB_PLACEHOLDERS: Record<string, string> = {
  world: `{
  "era": "现代",
  "region": "一线城市",
  "rules": ["现实主义", "权力差"]
}`,
  characters: `[
  {
    "id": "c_1",
    "name": "林晚晴",
    "archetype": "逆袭弱者",
    "arc": "觉醒 → 反击 → 高光",
    "tags": ["职场", "白领"]
  }
]`,
  npc: `[
  { "id": "npc_1", "name": "母亲", "function": "推动主角动机" }
]`,
  outline: `[
  {
    "id": "o_1",
    "title": "第 1-6 集 · 起点",
    "summary": "弱者被欺压,埋下反转钩子",
    "durationSec": 180
  }
]`,
  conflict: `[
  { "a": "林晚晴", "b": "陆景珩", "intensity": 0.75 }
]`,
  hooks: `[
  { "sceneId": "s1", "kind": "cliffhanger", "density": 0.8 }
]`,
  mood: `[
  { "t": 0.0, "arousal": 0.4, "valence": -0.2 }
]`,
  risk: `[
  { "code": "audience_drift", "level": "info", "message": "情节略偏小众" }
]`,
};

function EditableTab({
  episodeId,
  tab,
  value,
  render,
}: {
  episodeId: string;
  tab: 'world' | 'characters' | 'npc' | 'outline' | 'conflict' | 'hooks' | 'mood' | 'risk';
  value: unknown;
  render: (v: unknown) => React.ReactNode;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>('');
  const [parseError, setParseError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async (parsed: unknown) =>
      api
        .patch(`writer/atlas/${episodeId}/${tab}`, { json: { value: parsed } })
        .json<{ tab: string; updatedAt: string }>(),
    onSuccess: () => {
      toast.success('已保存', `Atlas 「${tab}」 已更新`);
      qc.invalidateQueries({ queryKey: ['atlas', episodeId] });
      setEditing(false);
    },
    onError: (err: Error) => toast.error('保存失败', err.message || '稍后重试'),
  });

  const enterEdit = () => {
    const has =
      (Array.isArray(value) && value.length > 0) ||
      (value && typeof value === 'object' && Object.keys(value as object).length > 0);
    setDraft(
      has ? JSON.stringify(value, null, 2) : TAB_PLACEHOLDERS[tab] ?? '[]',
    );
    setParseError(null);
    setEditing(true);
  };

  const save = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : '无效 JSON');
      return;
    }
    setParseError(null);
    mut.mutate(parsed);
  };

  if (!editing) {
    return (
      <div className="space-y-2">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={enterEdit}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-neutral-600 transition hover:bg-surface-variant hover:text-brand-600"
          >
            <Pencil className="h-3 w-3" /> 编辑
          </button>
        </div>
        {render(value)}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-outline">
        <span>JSON · 直接编辑后保存</span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(false)}
            disabled={mut.isPending}
          >
            <X className="h-3 w-3" /> 取消
          </Button>
          <Button
            variant="agent"
            agentColor="writer"
            size="sm"
            onClick={save}
            disabled={mut.isPending}
          >
            {mut.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            保存
          </Button>
        </div>
      </div>
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={14}
        className="w-full rounded-md border border-brand-300 bg-surface p-2 font-mono text-[11px] leading-relaxed text-neutral-800 outline-none focus:ring-2 focus:ring-brand-300"
        spellCheck={false}
      />
      {parseError && (
        <p className="rounded-md bg-danger-soft/40 px-2 py-1 text-[11px] text-danger">
          JSON 解析失败:{parseError}
        </p>
      )}
      <p className="text-[10px] text-neutral-400 leading-relaxed">
        提示:必填字段(如 characters.id / hooks.sceneId / mood.t)若缺失,后端会自动补齐;array
        / object 类型需匹配 schema。
      </p>
    </div>
  );
}

function AudienceTab({ audience, onEdit }: { audience?: AtlasAudience; onEdit: () => void }) {
  const locked = Boolean(audience?.locked);
  const tags = [
    audience?.primary,
    audience?.gender && audience.gender !== 'all'
      ? audience.gender === 'female'
        ? '女性向'
        : '男性向'
      : null,
    audience?.ageRange ? `${audience.ageRange[0]}-${audience.ageRange[1]} 岁` : null,
    ...(audience?.regions ?? []),
    ...(audience?.tags ?? []),
  ].filter(Boolean) as string[];
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs">
          <Lock className={locked ? 'h-4 w-4 text-success' : 'h-4 w-4 text-warning'} />
          <span className={locked ? 'font-semibold text-success' : 'text-warning'}>
            {locked ? '已锁定' : '未锁定'}
          </span>
        </div>
        <Button variant="secondary" size="sm" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" /> {locked ? '修改' : '锁定画像'}
        </Button>
      </div>
      {locked && tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {tags.map((t) => (
            <span key={t} className="rounded-full bg-success/10 px-2 py-0.5 text-xs text-success">
              {t}
            </span>
          ))}
        </div>
      ) : (
        <Empty hint={locked ? '已锁定但未设置标签' : '点击「锁定画像」开始'} />
      )}
      {audience?.secondary && audience.secondary.length > 0 && (
        <Field label="次要人群">
          <div className="flex flex-wrap gap-1">
            {audience.secondary.map((s) => (
              <span key={s} className="rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
                {s}
              </span>
            ))}
          </div>
        </Field>
      )}
      <div className="rounded-lg bg-brand-50/60 p-3 text-xs leading-relaxed text-brand-700">
        画像锁定后,后续 Path / Atlas 内容会按此目标人群差异化。
      </div>
    </div>
  );
}

function WorldTab({ world }: { world?: AtlasWorld }) {
  if (
    !world ||
    (!world.setting && !world.era && !world.region && !world.tone && (!world.rules || world.rules.length === 0) && (!world.themes || world.themes.length === 0))
  ) {
    return <Empty hint="自动生成 SSML 后将填充世界观" />;
  }
  return (
    <div className="space-y-3">
      {world.setting && <Field label="故事背景">{world.setting}</Field>}
      {world.era && <Field label="时代">{world.era}</Field>}
      {world.region && <Field label="地域">{world.region}</Field>}
      {world.tone && <Field label="基调">{world.tone}</Field>}
      {world.rules && world.rules.length > 0 && (
        <Field label="世界规则">
          <ul className="list-disc space-y-0.5 pl-5 text-sm">
            {world.rules.map((r) => <li key={r}>{r}</li>)}
          </ul>
        </Field>
      )}
      {world.themes && world.themes.length > 0 && (
        <Field label="核心主题">
          <div className="flex flex-wrap gap-1">
            {world.themes.map((t) => (
              <span key={t} className="rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
                {t}
              </span>
            ))}
          </div>
        </Field>
      )}
    </div>
  );
}

function CharactersTab({ list }: { list: AtlasCharacter[] }) {
  if (list.length === 0) return <Empty hint="尚未抽取人物 · 立项或生成后填充" />;
  return (
    <div className="space-y-3">
      {list.map((c) => (
        <div key={c.id} className="rounded-lg border border-outline-variant/20 bg-surface p-3 text-sm">
          <div className="mb-1.5">
            <div className="flex items-center gap-2">
              <strong className="text-base text-neutral-900">{c.name}</strong>
              {c.role && (
                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700">
                  {c.role}
                </span>
              )}
            </div>
            {c.archetype && (
              <p className="mt-0.5 text-xs text-neutral-500">{c.archetype}</p>
            )}
          </div>

          {c.arc && (
            <p className="mb-2 text-xs leading-relaxed text-neutral-600">
              <span className="mr-1 font-medium text-neutral-500">弧光</span>{c.arc}
            </p>
          )}

          {(c.desire || c.fear || c.flaw || c.contradiction) && (
            <div className="mb-2 space-y-1 rounded-md bg-surface-variant/40 px-2.5 py-2 text-xs">
              {c.desire && (
                <p><span className="font-medium text-neutral-500">渴望 </span><span className="text-neutral-700">{c.desire}</span></p>
              )}
              {c.fear && (
                <p><span className="font-medium text-neutral-500">恐惧 </span><span className="text-neutral-700">{c.fear}</span></p>
              )}
              {c.flaw && (
                <p><span className="font-medium text-neutral-500">缺陷 </span><span className="text-neutral-700">{c.flaw}</span></p>
              )}
              {c.contradiction && (
                <p><span className="font-medium text-neutral-500">矛盾 </span><span className="text-neutral-700">{c.contradiction}</span></p>
              )}
            </div>
          )}

          {(c.speaking_style || c.subtext) && (
            <div className="mb-2 space-y-1 text-xs">
              {c.speaking_style && (
                <p><span className="font-medium text-neutral-500">说话风格 </span><span className="text-neutral-600">{c.speaking_style}</span></p>
              )}
              {c.subtext && (
                <p><span className="font-medium text-neutral-500">潜台词 </span><span className="text-neutral-600">{c.subtext}</span></p>
              )}
            </div>
          )}

          {c.relations && c.relations.length > 0 && (
            <div className="mb-2">
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">人物关系</p>
              <div className="space-y-1">
                {c.relations.map((r, i) => (
                  <div key={i} className="rounded bg-surface-variant/30 px-2 py-1 text-xs">
                    <span className="font-medium text-brand-600">{r.target}</span>
                    <span className="mx-1 text-neutral-400">·</span>
                    <span className="text-neutral-500">{r.kind}</span>
                    {r.dynamic && <p className="mt-0.5 text-neutral-600">{r.dynamic}</p>}
                    {r.turning_point && (
                      <p className="mt-0.5 text-neutral-400">转折：{r.turning_point}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {c.tags && c.tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {c.tags.map((t) => (
                <span key={t} className="rounded bg-surface-variant/60 px-1.5 py-0.5 text-[10px] text-neutral-600">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function NpcTab({ list }: { list: AtlasNpc[] }) {
  if (list.length === 0) return <Empty hint="NPC 待生成…" />;
  return (
    <div className="space-y-2">
      {list.map((n) => (
        <div key={n.id} className="rounded-lg border border-outline-variant/20 bg-surface p-2 text-sm">
          <strong>{n.name}</strong>
          {n.function && <span className="ml-2 text-xs text-outline">{n.function}</span>}
        </div>
      ))}
    </div>
  );
}

function OutlineTab({ list }: { list: AtlasOutlineNode[] }) {
  if (list.length === 0) return <Empty hint="待自动生成大纲" />;
  return (
    <ol className="space-y-2 text-sm">
      {list.map((o, i) => (
        <li
          key={o.id}
          className="flex gap-2 rounded-lg border border-outline-variant/30 bg-surface-variant/30 px-3 py-2"
        >
          <span className="shrink-0 text-xs text-brand-600">#{i + 1}</span>
          <div className="min-w-0 flex-1">
            <strong className="text-neutral-900">{o.title}</strong>
            {o.summary && <p className="text-xs text-neutral-600">{o.summary}</p>}
            {o.durationSec ? (
              <p className="mt-0.5 text-[10px] text-outline">建议时长 {o.durationSec}s</p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

function ConflictTab({ list }: { list: AtlasConflictCell[] }) {
  if (list.length === 0) return <Empty hint="冲突矩阵待生成" />;
  return (
    <div className="space-y-2">
      <p className="text-xs text-outline">人物冲突强度 (0-1)</p>
      <div className="space-y-1.5">
        {list.map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-20 shrink-0 truncate text-neutral-700">{c.a}</span>
            <span className="text-outline">×</span>
            <span className="w-20 shrink-0 truncate text-neutral-700">{c.b}</span>
            <div className="ml-auto h-2 w-24 overflow-hidden rounded-full bg-surface-variant">
              <div
                className="h-full rounded-full bg-danger/70"
                style={{ width: `${Math.min(100, c.intensity * 100)}%` }}
              />
            </div>
            <span className="w-8 text-right text-[10px] text-neutral-500">
              {Math.round(c.intensity * 100)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HooksTab({ list }: { list: AtlasHook[] }) {
  if (list.length === 0) return <Empty hint="爽点分布待生成" />;
  const avgDensity = list.reduce((s, h) => s + h.density, 0) / list.length;
  const sorted = [...list].sort((a, b) => (a.episode ?? 0) - (b.episode ?? 0));
  const maxEp = Math.max(...sorted.map((h) => h.episode ?? 0), 1);
  const kindColor: Record<string, { bg: string; fg: string; bar: string; label: string }> = {
    cliffhanger: { bg: 'bg-danger-soft/40', fg: 'text-danger', bar: 'bg-danger', label: '悬念' },
    reveal: { bg: 'bg-brand-50', fg: 'text-brand-700', bar: 'bg-brand-500', label: '揭示' },
    twist: { bg: 'bg-fuchsia-50', fg: 'text-fuchsia-700', bar: 'bg-fuchsia-500', label: '反转' },
    tease: { bg: 'bg-amber-50', fg: 'text-amber-700', bar: 'bg-amber-500', label: '挑逗' },
  };
  const kindOf = (k: string) =>
    kindColor[k] ?? { bg: 'bg-surface-variant', fg: 'text-neutral-600', bar: 'bg-neutral-400', label: k };
  const kinds = Array.from(new Set(sorted.map((h) => h.kind)));
  // 钩子聚集度:平均 episode 间隔
  const epList = sorted.map((h) => h.episode ?? 0).filter((e) => e > 0);
  const avgGap =
    epList.length >= 2
      ? (epList[epList.length - 1] - epList[0]) / (epList.length - 1)
      : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-lg bg-warning-soft/40 px-2 py-1.5">
          <div className="text-[10px] text-outline">平均密度</div>
          <div className={`text-sm font-semibold ${avgDensity >= 0.5 ? 'text-success' : 'text-warning'}`}>
            {avgDensity.toFixed(2)}
            <span className="ml-1 text-[10px] font-normal text-neutral-500">/ 1.00</span>
          </div>
        </div>
        <div className="rounded-lg bg-brand-50/60 px-2 py-1.5">
          <div className="text-[10px] text-outline">钩子总数</div>
          <div className="text-sm font-semibold text-brand-700">
            {sorted.length}
            <span className="ml-1 text-[10px] font-normal text-neutral-500">个</span>
          </div>
        </div>
        <div className="rounded-lg bg-surface-variant/40 px-2 py-1.5">
          <div className="text-[10px] text-outline">平均间隔</div>
          <div className="text-sm font-semibold text-neutral-800">
            {avgGap > 0 ? avgGap.toFixed(1) : '-'}
            <span className="ml-1 text-[10px] font-normal text-neutral-500">集 / 钩</span>
          </div>
        </div>
      </div>

      {/* 钩子类型图例 */}
      <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
        {kinds.map((k) => {
          const c = kindOf(k);
          return (
            <span key={k} className={`inline-flex items-center gap-1 rounded-full ${c.bg} px-2 py-0.5 ${c.fg}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${c.bar}`} />
              {c.label}
            </span>
          );
        })}
      </div>

      {/* 时间线:横轴=集数,纵轴柱高=密度 */}
      <div>
        <div className="mb-1 flex items-center justify-between text-[10px] text-outline">
          <span>钩子时间线</span>
          <span>共 {maxEp} 集</span>
        </div>
        <div className="relative h-24 rounded-lg bg-surface-variant/30 px-2 pt-2 pb-4">
          <div className="absolute inset-x-2 bottom-4 h-px bg-outline-variant/50" />
          {sorted.map((h, i) => {
            const ep = h.episode ?? 0;
            const left = maxEp > 1 ? ((ep - 1) / (maxEp - 1)) * 100 : 50;
            const c = kindOf(h.kind);
            const height = Math.max(8, Math.min(100, h.density * 100));
            return (
              <div
                key={i}
                title={`第 ${ep} 集 · ${c.label} · 密度 ${h.density.toFixed(2)}${h.desc ? '\n' + h.desc : ''}`}
                className="absolute bottom-4 -translate-x-1/2"
                style={{ left: `${left}%` }}
              >
                <div className={`w-1.5 rounded-t ${c.bar}`} style={{ height: `${height * 0.55}px` }} />
              </div>
            );
          })}
          {/* x 轴刻度 */}
          <div className="pointer-events-none absolute inset-x-2 bottom-0 flex justify-between text-[9px] text-outline">
            <span>1</span>
            <span>{Math.ceil(maxEp / 2)}</span>
            <span>{maxEp}</span>
          </div>
        </div>
      </div>

      {/* 钩子明细列表 */}
      <ul className="max-h-44 space-y-1 overflow-y-auto pr-1">
        {sorted.map((h, i) => {
          const c = kindOf(h.kind);
          return (
            <li key={i} className={`flex items-start gap-2 rounded-lg ${c.bg} px-2.5 py-1.5 text-xs`}>
              <span className={`mt-0.5 inline-flex h-5 w-9 shrink-0 items-center justify-center rounded ${c.bar} text-[10px] font-semibold text-white`}>
                E{h.episode ?? '?'}
              </span>
              <div className="flex-1 leading-snug">
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] font-medium ${c.fg}`}>{c.label}</span>
                  <span className="text-[10px] text-neutral-400">·</span>
                  <span className={`text-[10px] tabular-nums ${c.fg}`}>{Math.round(h.density * 100)}%</span>
                </div>
                {h.desc && <p className="mt-0.5 text-neutral-700">{h.desc}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function MoodTab({ list }: { list: AtlasMoodPoint[] }) {
  if (list.length === 0) return <Empty hint="情绪曲线待 SSE 流入…" />;
  const sorted = [...list].sort((a, b) => (a.episode ?? a.t * 100) - (b.episode ?? b.t * 100));
  const xs = sorted.map((m, i) => m.episode ?? i + 1);
  const maxEp = Math.max(...xs, 1);
  const minEp = Math.min(...xs, 1);
  const W = 280;
  const H = 110;
  const PAD_L = 22;
  const PAD_R = 8;
  const PAD_T = 8;
  const PAD_B = 18;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const xFor = (ep: number) =>
    PAD_L + (maxEp > minEp ? ((ep - minEp) / (maxEp - minEp)) * innerW : innerW / 2);
  // valence ∈ [-1,1] → 全高居中;arousal ∈ [0,1] → 下半区
  const yValence = (v: number) => PAD_T + innerH * (1 - (v + 1) / 2);
  const yArousal = (a: number) => PAD_T + innerH * (1 - a);
  const polyValence = sorted.map((m, i) => `${xFor(xs[i])},${yValence(m.valence)}`).join(' ');
  const polyArousal = sorted.map((m, i) => `${xFor(xs[i])},${yArousal(m.arousal)}`).join(' ');
  const zeroY = PAD_T + innerH / 2;

  // 高潮点 / 低谷点提取
  const peakArousal = sorted.reduce(
    (p, c, i) => (c.arousal > p.val ? { val: c.arousal, ep: xs[i], label: c.label } : p),
    { val: -1, ep: 0, label: '' as string | undefined },
  );
  const valleyValence = sorted.reduce(
    (p, c, i) => (c.valence < p.val ? { val: c.valence, ep: xs[i], label: c.label } : p),
    { val: 2, ep: 0, label: '' as string | undefined },
  );

  return (
    <div className="space-y-3">
      {/* 图表 */}
      <div>
        <div className="mb-1 flex items-center justify-between text-[10px] text-outline">
          <span>情绪曲线 (按集采样)</span>
          <span className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <span className="h-0.5 w-3 bg-success" /> 价(正向)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-0.5 w-3 bg-brand-500" /> 唤起(强度)
            </span>
          </span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full overflow-visible rounded-lg bg-surface-variant/30">
          {/* 坐标轴 */}
          <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={H - PAD_B} stroke="currentColor" strokeOpacity="0.2" />
          <line x1={PAD_L} y1={H - PAD_B} x2={W - PAD_R} y2={H - PAD_B} stroke="currentColor" strokeOpacity="0.2" />
          {/* 价的 0 线 (中线) */}
          <line
            x1={PAD_L}
            y1={zeroY}
            x2={W - PAD_R}
            y2={zeroY}
            stroke="currentColor"
            strokeOpacity="0.15"
            strokeDasharray="3 3"
          />
          {/* y 轴标签 */}
          <text x={4} y={PAD_T + 4} fontSize="9" fill="currentColor" opacity="0.5">
            +1
          </text>
          <text x={6} y={zeroY + 3} fontSize="9" fill="currentColor" opacity="0.5">
            0
          </text>
          <text x={4} y={H - PAD_B} fontSize="9" fill="currentColor" opacity="0.5">
            -1
          </text>
          {/* 价折线 */}
          <polyline
            fill="none"
            stroke="rgb(34,197,94)"
            strokeWidth="1.5"
            strokeLinejoin="round"
            points={polyValence}
          />
          {/* 唤起折线 */}
          <polyline
            fill="none"
            stroke="rgb(99,102,241)"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeDasharray="4 2"
            points={polyArousal}
          />
          {/* 数据点 + 标签 */}
          {sorted.map((m, i) => {
            const x = xFor(xs[i]);
            return (
              <g key={i}>
                <circle cx={x} cy={yValence(m.valence)} r={2} fill="rgb(34,197,94)" />
                <circle cx={x} cy={yArousal(m.arousal)} r={2} fill="rgb(99,102,241)" />
                {m.label && (
                  <text
                    x={x}
                    y={H - 4}
                    fontSize="9"
                    fill="currentColor"
                    opacity="0.6"
                    textAnchor="middle"
                  >
                    {m.label.slice(0, 4)}
                  </text>
                )}
              </g>
            );
          })}
          {/* x 轴刻度 */}
          <text x={PAD_L} y={H - PAD_B + 10} fontSize="9" fill="currentColor" opacity="0.45">
            E{minEp}
          </text>
          <text
            x={W - PAD_R}
            y={H - PAD_B + 10}
            fontSize="9"
            fill="currentColor"
            opacity="0.45"
            textAnchor="end"
          >
            E{maxEp}
          </text>
        </svg>
      </div>

      {/* 解读提示 */}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg bg-brand-50/60 px-2 py-1.5">
          <div className="text-[10px] text-outline">情绪最强点</div>
          <div className="font-semibold text-brand-700">
            E{peakArousal.ep} · {peakArousal.label || '—'}
          </div>
          <div className="text-[10px] text-neutral-500">A={peakArousal.val.toFixed(2)}</div>
        </div>
        <div className="rounded-lg bg-danger-soft/30 px-2 py-1.5">
          <div className="text-[10px] text-outline">情绪最低点</div>
          <div className="font-semibold text-danger">
            E{valleyValence.ep} · {valleyValence.label || '—'}
          </div>
          <div className="text-[10px] text-neutral-500">V={valleyValence.val.toFixed(2)}</div>
        </div>
      </div>

      {/* 详细采样点 */}
      <ul className="max-h-32 space-y-1 overflow-y-auto pr-1 text-xs">
        {sorted.map((m, i) => (
          <li
            key={i}
            className="flex items-center gap-2 rounded-md bg-surface-variant/30 px-2 py-1"
          >
            <span className="inline-flex h-5 w-9 shrink-0 items-center justify-center rounded bg-neutral-200 text-[10px] font-semibold text-neutral-700">
              E{xs[i]}
            </span>
            {m.label && <span className="text-neutral-700">{m.label}</span>}
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] tabular-nums">
              <span className={m.valence >= 0 ? 'text-success' : 'text-danger'}>
                V {m.valence >= 0 ? '+' : ''}
                {m.valence.toFixed(2)}
              </span>
              <span className="text-outline">·</span>
              <span className="text-brand-600">A {m.arousal.toFixed(2)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RiskTab({ list }: { list: AtlasRiskItem[] }) {
  if (list.length === 0) return <Empty hint="合规检查通过 · 无标记" />;
  const tone = (lvl: AtlasRiskItem['level']) =>
    lvl === 'block'
      ? 'bg-danger-soft/40 text-danger'
      : lvl === 'warn'
        ? 'bg-warning-soft/40 text-warning'
        : 'bg-brand-50 text-brand-700';
  return (
    <div className="space-y-1.5">
      {list.map((r, i) => (
        <div key={i} className={`rounded-lg px-2.5 py-2 text-xs ${tone(r.level)}`}>
          <div className="flex items-center justify-between">
            <strong>{r.code}</strong>
            <span className="text-[10px] uppercase">{r.level}</span>
          </div>
          <p className="mt-0.5 leading-relaxed">{r.message}</p>
        </div>
      ))}
    </div>
  );
}
